import { useState } from 'react'
import { useOpenWindow } from '@krp/client-runtime'
import { CASHIER_GROUPS_WINDOWS, CashierPicker, CashierReportScreen, receiptsGroupPage, receiptsGroupSummary, receiptsGroups, receiptsMyCashierBinding } from '@krp/resource-receipts-ui'
import { StatusMessage, firstOfMonth } from '@krp/ui-kit'
import { useMyAccess } from '../platform/useMyAccess'
import { ReceiptLinesByPath } from './ReceiptLinesByPath'
import { SalesStatisticsGate, SalesStatisticsTabs } from './SalesStatisticsTabs'
import { errorText, useOriginal, useReportRange } from './reportHooks'

/**
 * Таб «Кассиры» раздела «Статистика по продажам» (RP-STAT-UNIFY): сводная статистика нашего кассира по всем его связям — без кода кассира.
 * Привязанный к аккаунту кассир подставлен по умолчанию. Тот, у кого есть право видеть любого кассира (`platform:sales:cashiers:any`), выбирает
 * из списка (первые десять и поиск по имени); без права выбор заблокирован — виден только свой кассир. Сервер проверяет это сам.
 */
export function CashierStatisticsBinding() {
  return <><SalesStatisticsTabs selected="cashiers" /><SalesStatisticsGate><CashierStatistics /></SalesStatisticsGate></>
}

function CashierStatistics() {
  const open = useOpenWindow()
  const access = useMyAccess()
  const { intent, today, from, to, patch } = useReportRange()
  const binding = receiptsMyCashierBinding.useQuery()
  const directory = receiptsGroups.useQuery()
  // Выбор человека; `null` — не трогал, подставлен привязанный к аккаунту кассир. Пока привязка не прочитана, выбор закрыт.
  const [chosen, setChosen] = useState<string | null>(null)
  const bindingReady = binding.currentData !== undefined
  const locked = !bindingReady || binding.currentData?.canChooseAny !== true
  const recommended = binding.currentData?.personId ?? ''
  const personId = locked ? recommended : chosen ?? recommended
  // Кассиры нашей системы (не в архиве): выбор — по человеку, а не по источнику. Статистика суммирует все его связи.
  const directoryPeople = (directory.currentData?.people ?? []).filter((person) => !person.archived).map((person) => ({ id: person.id, name: person.name }))
  const bound = binding.currentData?.person
  // Без права выбирать любого в списке только привязанный кассир (нет привязки — список пуст).
  const people = !locked ? directoryPeople : bound == null ? [] : [bound]
  const month = firstOfMonth(from)
  // Порядок чтения: сначала выбранный день (по умолчанию — сегодня), потом весь месяц для графика.
  const singleDay = from === to
  const groupArgs = { id: personId, month, timeZoneId: intent.timeZoneId, refresh: intent.refresh }
  const noPerson = personId === ''
  const monthState = receiptsGroupSummary.useQueryState(groupArgs, { skip: noPerson })
  const dayGroup = receiptsGroupSummary.useQuery({ ...groupArgs, from, to }, { skip: noPerson || !singleDay || monthState.isSuccess })
  const dayDone = !singleDay || dayGroup.isSuccess || dayGroup.isError || monthState.isSuccess
  const groupSummary = receiptsGroupSummary.useQuery(groupArgs, { skip: noPerson || !dayDone })
  const monthData = groupSummary.isError ? undefined : groupSummary.currentData
  const dayData = dayGroup.currentData
  // Месяц пришёл — итоги и график из него; пока его нет — из дня. Чеки читаются по снимку того отчёта, что на экране.
  const data = noPerson ? undefined : monthData ?? dayData
  const monthPending = !noPerson && !groupSummary.isSuccess && !groupSummary.isError
  const busy = directory.isFetching || binding.isFetching || dayGroup.isFetching || (data === undefined && groupSummary.isFetching)
  const trendBusy = groupSummary.isFetching || monthPending
  const pageArgs = { id: personId, snapshotId: data?.snapshotId ?? '', from, to, place: intent.place, kind: intent.kind, search: intent.search, offset: intent.offset }
  const page = receiptsGroupPage.useQuery(pageArgs, { skip: data === undefined })
  const original = useOriginal()
  const error = errorText(binding.error ?? directory.error ?? groupSummary.error ?? (data === undefined ? dayGroup.error : undefined) ?? page.error) ?? original.error
  const unbound = bindingReady && locked && recommended === ''
  return <>
    {access.data !== undefined && unbound && <StatusMessage kind="forbidden" title="Кассир не привязан к аккаунту" text="Кассир не привязан к аккаунту, обратитесь к администратору." />}
    <CashierReportScreen personId={personId} range={{ from, to }} today={today} timeZoneId={intent.timeZoneId} place={intent.place} kind={intent.kind} search={intent.search}
      directory={directory.currentData} cashierOptions={people.map((person) => ({ id: person.id, label: person.name }))}
      cashierControl={<CashierPicker people={people} value={personId} locked={locked} onChange={setChosen} />}
      summary={data} page={data === undefined || page.isError ? undefined : page.currentData} offset={intent.offset} busy={busy} trendBusy={trendBusy} pageBusy={page.isFetching}
      dailyTable receiptsOpen={false}
      error={error}
      patch={(value) => { if (value.personId !== undefined) { setChosen(value.personId); patch({ place: '', offset: 0 }); return } patch(value) }}
      reload={() => patch({ refresh: intent.refresh + 1 })} links={() => open(CASHIER_GROUPS_WINDOWS.report)} original={original.original}
      receiptLines={(item) => <ReceiptLinesByPath originalPath={item.receipt.originalPath} />} />
  </>
}
